<template>
  <div>
    <div class="p-2">
      <div v-if="!order.id" class="p-3 text-center text-muted">No order selected</div>
      <div v-else>
        <div class="verify-header d-flex justify-content-between align-items-start mb-3">
          <div>
            <div class="small text-muted">Vendor</div>
            <div class="h6 mb-1">{{ order.vendor?.first_name || '' }} {{ order.vendor?.last_name || '' }}</div>
            <div class="small text-muted">{{ order.vendor?.company_name || order.vendor?.external_id || '' }}</div>
            <div class="small mt-1">{{ order.vendor?.phone || '' }}{{ order.vendor?.email ? ' • ' + order.vendor.email : '' }}</div>
            <div class="mt-3 verify-po-status">
              <PoStatus :status="order.receive_status || order.status || 'pending'" :showIcon="true" />
            </div>
          </div>
          <div class="text-end">
            <div class="small text-muted">Purchase Order</div>
            <div class="h6 mb-1">{{ order.name || order.external_id }}</div>
            <div class="small">PO#: {{ order.external_id || '-' }}</div>
            <div class="small">Created: {{ order.created_at || '-' }}</div>
            <div class="small">Expected: {{ order.expected_delivery_date || '-' }}</div>
            <div class="h6 mt-2">{{ order.total_price ? ('$' + Number(order.total_price).toFixed(2)) : '-' }}</div>
          </div>
        </div>

        <div class="mb-3">
          <div class="small text-muted">Order Description</div>
          <div class="text-dark">{{ order.description || order.notes || '-' }}</div>
        </div>

        <div class="verify-items-list mb-3">
          <template v-if="form.items.length">
            <div v-for="(item, index) in form.items" :key="item.itemable_id || index" class="verify-item-card mb-4">
              <div class="verify-item-header d-flex flex-column flex-md-row justify-content-between gap-3 p-3">
                <div class="d-flex align-items-center gap-3">
                  <span class="item-number">{{ index + 1 }}</span>
                  <div>
                    <div class="item-name text-dark">{{ item.name }}</div>
                    <div class="item-code text-muted small">{{ item.code }}</div>
                  </div>
                </div>
                <div class="verify-item-meta d-flex flex-wrap gap-2 text-center">
                  <div class="meta-block">
                    <div class="meta-label">Ordered</div>
                    <div class="meta-value">{{ item.quantity }} {{ item.unit }}</div>
                  </div>
                  <div class="meta-block">
                    <div class="meta-label">Received</div>
                    <div class="meta-value">{{ item.received_quantity !== null ? item.received_quantity : '-' }} {{ item.unit }}</div>
                  </div>
                  <div class="meta-block">
                    <div class="meta-label">Status</div>
                    <div>
                      <span v-if="item.isMatched" class="badge bg-success">Matched</span>
                      <span v-else class="badge bg-warning text-dark">Not matched</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="item.material_type === 'chemical' || item.material_type === 'part'" class="verify-item-flags p-3 pt-0">
                <div class="d-flex flex-wrap align-items-center gap-4">
                  <template v-if="item.material_type === 'chemical'">
                    <div class="d-flex align-items-center gap-2 text-dark">
                      <i :class="item.sds === 'yes' ? 'fa fa-check-circle text-success' : 'fa fa-times-circle text-danger'"></i>
                      <span>Take Safety Data Sheet (SDS) at receiving PO? <span class="fw-semibold">{{ item.sds === 'yes' ? 'Yes' : 'No' }}</span></span>
                    </div>
                    <div class="d-flex align-items-center gap-2 text-dark">
                      <i :class="item.approved_chemical === 'yes' ? 'fa fa-check-circle text-success' : 'fa fa-times-circle text-danger'"></i>
                      <span>Is it Approved Chemical? <span class="fw-semibold">{{ item.approved_chemical === 'yes' ? 'Yes' : 'No' }}</span></span>
                    </div>
                    <div class="d-flex align-items-center gap-2 text-dark">
                      <i :class="item.food_grade_chemical === 'yes' ? 'fa fa-check-circle text-success' : 'fa fa-times-circle text-danger'"></i>
                      <span>Is it Food Grade chemical? <span class="fw-semibold">{{ item.food_grade_chemical === 'yes' ? 'Yes' : 'No' }}</span></span>
                    </div>
                    <div class="d-flex align-items-center gap-2 text-dark">
                      <span>SDS File:</span>
                      <span>
                        <a v-if="item.sds_attachment" :href="resolveFileUrl(item.sds_attachment)" target="_blank" class="text-primary text-decoration-none">View SDS</a>
                        <span v-else class="text-muted">N/A</span>
                      </span>
                    </div>
                  </template>
                  <template v-else-if="item.material_type === 'part'">
                    <div class="d-flex align-items-center gap-2 text-dark">
                      <i :class="item.food_contact_part === 'yes' ? 'fa fa-check-circle text-success' : 'fa fa-times-circle text-danger'"></i>
                      <span>Is it Food Contact part? <span class="fw-semibold">{{ item.food_contact_part === 'yes' ? 'Yes' : 'No' }}</span></span>
                    </div>
                  </template>
                </div>
              </div>
              <div v-if="item.checklists && item.checklists.length" class="verify-checklist-card p-3">
                <div class="verify-checklist-title mb-3">Equipment Sanitation Checklist</div>
                <div class="verify-checklist-grid">
                  <div v-for="(check, cIdx) in item.checklists" :key="check.id || cIdx" class="verify-checklist-row">
                    <div class="verify-checklist-index">{{ cIdx + 1 }}</div>
                    <div class="verify-checklist-label">{{ check.label }}</div>
                    <div class="verify-checklist-value">
                      <span style="text-transform: uppercase;" :class="['badge', check.value === 'yes' ? 'bg-success' : check.value === 'no' ? 'bg-danger' : 'bg-warning text-dark']">
                        {{ check.value === 'na' ? 'N/A' : check.value }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <div v-else class="text-center text-muted py-3">No items found</div>
        </div>

        <div class="receive-summary mb-4">
          <div class="row gx-3 gy-2">
            <div class="col-md-4">
              <div class="small text-muted mb-1">Receive Status</div>
              <div class="fw-semibold text-dark">{{ receiveStatusLabel }}</div>
            </div>
            <div class="col-md-4">
              <div class="small text-muted mb-1">Received By</div>
              <div class="text-dark">{{ receivedBy }}</div>
            </div>
            <div class="col-md-4">
              <div class="small text-muted mb-1">Receive Notes</div>
              <div class="text-dark">{{ order.receiver_notes || order.notes || '-' }}</div>
            </div>
          </div>
        </div>

        <!-- Images Section -->
        <div v-if="order.images && order.images.length" class="mb-4">
          <label class="form-label form-label-thin">Images</label>
          <div class="d-flex flex-wrap gap-2">
            <div v-for="image in order.images" :key="image.id" class="attachment-tile text-center" style="width: 100px; cursor: pointer;" @click="openFile(image.file_path)">
              <div class="attachment-preview mb-2 rounded border d-flex align-items-center justify-content-center" style="width: 100%; height: 80px; background-color: #f0f0f0; overflow: hidden;">
                <img :src="getFileUrl(image.file_path)" :alt="image.file_name" style="max-width: 100%; max-height: 100%; object-fit: cover;" />
              </div>
              <small class="text-truncate" style="display: block; width: 100%; font-size: 11px;">{{ image.file_name }}</small>
              <small class="text-muted" style="display: block; font-size: 10px;">{{ formatFileSize(image.file_size) }}</small>
            </div>
          </div>
        </div>

        <!-- Files Section -->
        <div v-if="order.files && order.files.length" class="mb-4">
          <label class="form-label form-label-thin">Files</label>
          <div class="d-flex flex-wrap gap-2">
            <div v-for="file in order.files" :key="file.id" class="attachment-tile text-center" style="width: 100px; cursor: pointer;" @click="openFile(file.file_path)">
              <div class="attachment-preview mb-2 rounded border d-flex align-items-center justify-content-center" style="width: 100%; height: 80px; background-color: #f8f9fa;">
                <div>
                  <i :class="getFileIcon(file.file_name)" style="font-size: 32px; color: #667eea;"></i>
                </div>
              </div>
              <small class="text-truncate" style="display: block; width: 100%; font-size: 11px;">{{ file.file_name }}</small>
              <small class="text-muted" style="display: block; font-size: 10px;">{{ formatFileSize(file.file_size) }}</small>
            </div>
          </div>
        </div>

        <form @submit.prevent="submitVerify" id="verify_order_form">
          <div class="mb-4">
            <label class="form-label form-label-thin">Approval Status <span class="text-danger">*</span></label>
            <select class="form-select form-select-thin" name="approve_status" id="approve_status" ref="approve_status" v-model="form.approve_status"  >
              <option value="pending">Pending</option>
              <option value="rejected_returned">Rejected & Returned</option>
              <option value="approved" :disabled="!canApprove">Approved</option>
              <option value="partially_approved">Partially Approved</option>
            </select>
          </div>

          <div class="mb-4">
            <label class="form-label form-label-thin"> Notes <span class="text-danger">*</span></label>
            <textarea class="form-control form-control-thin" name="approve_notes" id="approve_notes" ref="approve_notes" v-model="form.approve_notes" rows="3" placeholder="Enter notes for verification"  ></textarea>
          </div>

          <div v-if="form.approve_status === 'rejected_returned'" class="mb-4">
            <label class="form-label form-label-thin">Rejection Remarks <span class="text-danger">*</span></label>
            <textarea class="form-control form-control-thin" name="rejection_remarks" id="rejection_remarks" ref="rejection_remarks" v-model="form.rejection_remarks" rows="3" placeholder="Enter rejection remarks"  ></textarea>
          </div>

          <div v-if="form.approve_status === 'rejected_returned'" class="mb-4">
            <label class="form-label form-label-thin">Return Date <span class="text-danger">*</span></label>
            <input type="date" class="form-control form-control-thin" name="return_date" id="return_date" ref="return_date" v-model="form.return_date"   />
          </div>

          <div v-if="form.approve_status === 'rejected_returned'" class="mb-4">
            <label class="form-label form-label-thin">Return Remarks <span class="text-danger">*</span></label>
            <textarea class="form-control form-control-thin" name="return_remarks" id="return_remarks" ref="return_remarks" v-model="form.return_remarks" rows="3" placeholder="Enter return remarks"  ></textarea>
          </div>
          <div class="mb-4">
            <LogsTimeline :logs="order.logs || []" />
          </div>
          <div class="d-flex justify-content-end gap-2">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="close">Cancel</button>
            <button type="submit" class="btn btn-sm btn-primary" :disabled="submitting" v-if="order.receive_status != null">
              {{ submitting ? 'Saving...' : 'Save Approval' }}
            </button>
            <button v-else class="btn btn-sm btn-secondary" disabled>
              Receiving Pending
            </button>
          </div>
        </form>

      
      </div>
    </div>
  </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import { resolveFileUrl as resolveAssetFileUrl } from '@/utils/fileUrl'
import PoStatus from '@/components/PoStatus.vue'
import LogsTimeline from '@/components/LogsTimeline.vue'

export default {
  name: 'VerifyOrder',
  components: {
    PoStatus,
    LogsTimeline
  },
  data() {
    return {
      order: {},
      form: {
        approve_status: 'pending',
        approve_notes: '',
        rejection_remarks: '',
        return_date: '',
        return_remarks: '',
        items: []
      },
      submitting: false
    }
  },
  computed: {
    canApprove() {
      return this.form.items.length > 0 && this.form.items.every(item => item.isMatched)
    },
    formattedTotal() {
      return this.order.total_price ? ('$' + Number(this.order.total_price).toFixed(2)) : '-'
    },
    receiveStatusLabel() {
      if (!this.order.receive_status) return '-'
      const labels = {
        received: 'Received',
        partial_received: 'Partially Received',
        pending: 'Pending'
      }
      return labels[String(this.order.receive_status).toLowerCase()] || this.order.receive_status
    },
    receivedBy() {
      return this.order.received_by?.first_name && this.order.received_by?.last_name 
        ? `${this.order.received_by.first_name} ${this.order.received_by.last_name}` 
        : '-' 
    }
  },
  methods: {
    setOrder(order) {
      this.order = order || {}
      this.form.items = (this.order.items || []).map(i => {
        const quantity = Number(i.quantity || 0)
        const received = i.received_quantity !== undefined && i.received_quantity !== null ? Number(i.received_quantity) : null
        return {
          itemable_id: i.itemable_id || i.itemable?.id || i.id,
          name: i.get_material?.name || i.get_materal?.name || i.get_materials?.name || i.itemable?.name || i.itemable_name || '',
          code: i.get_material?.code || i.get_materal?.code || i.itemable?.code || '',
          unit: i.unit || i.get_material?.unit || i.get_materal?.unit || i.itemable?.unit || '',
          quantity,
          received_quantity: received,
          isMatched: received !== null && received === quantity,
          material_type: i.material_type || '',
          sds: i.sds || 'no',
          approved_chemical: i.approved_chemical || 'no',
          food_grade_chemical: i.food_grade_chemical || 'no',
          food_contact_part: i.food_contact_part || 'no',
          sds_attachment: i.sds_attachment || i.sds_attachment_path || null,
          checklists: Array.isArray(i.checklists) ? i.checklists : []
        }
      })
      this.form.approve_status = 'pending'
      this.form.approve_notes = ''
      this.form.rejection_remarks = ''
      this.form.return_date = ''
      this.form.return_remarks = ''
    },
    close() {
      this.$emit('close')
    },
    getFileUrl(filePath) {
      return resolveAssetFileUrl(filePath)
    },
    resolveFileUrl(filePath) {
      return this.getFileUrl(filePath)
    },
    getFileIcon(fileName) {
      const ext = (fileName || '').split('.').pop().toLowerCase()
      const iconMap = {
        'pdf': 'fas fa-file-pdf',
        'doc': 'fas fa-file-word',
        'docx': 'fas fa-file-word',
        'xls': 'fas fa-file-excel',
        'xlsx': 'fas fa-file-excel',
        'ppt': 'fas fa-file-powerpoint',
        'pptx': 'fas fa-file-powerpoint',
        'txt': 'fas fa-file-text',
        'zip': 'fas fa-file-archive',
        'rar': 'fas fa-file-archive',
        'csv': 'fas fa-file-csv',
        'json': 'fas fa-file-code',
        'xml': 'fas fa-file-code',
        'jpg': 'fas fa-file-image',
        'jpeg': 'fas fa-file-image',
        'png': 'fas fa-file-image',
        'gif': 'fas fa-file-image',
        'webp': 'fas fa-file-image'
      }
      return iconMap[ext] || 'fas fa-file'
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
    },
    openFile(filePath) {
      const url = this.getFileUrl(filePath)
      window.open(url, '_blank')
    },
    async submitVerify() {
      this.submitting = true
      const payload = {
        purchase_order_id: this.order.id,
        approve_status: this.form.approve_status,
        approve_notes: this.form.approve_notes
      }

      if (this.form.approve_status === 'rejected_returned') {
        payload.rejection_remarks = this.form.rejection_remarks
        payload.return_date = this.form.return_date
        payload.return_remarks = this.form.return_remarks
      }

      const result = await form_api_submit({
        url: 'purchase-order/verify',
        method: 'POST',
        data: payload,
        curr_form_id: '#verify_order_form',
        show_success_toast: true,
        show_error_toast: true
      })

      if (result.success) {
        this.$emit('verified', result.data)
        this.close()
      }

      this.submitting = false
    }
  }
}
</script>

<style scoped>
.verify-header {
  border-bottom: 1px solid #eef2f6;
  padding-bottom: 12px;
}
.item-name { font-weight: 600 }
.item-code { font-family: monospace; font-size: 12px }
.invoice-style th {
  background: transparent;
  color: #374151;
  font-weight: 700;
}
.invoice-style td { vertical-align: middle }
.badge {
  padding: 6px 8px;
  border-radius: 999px;

}

.verify-items-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.verify-item-card {
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  background: #ffffff;
  overflow: hidden;
}

.verify-item-header {
  border-bottom: 1px solid #edf2f7;
  background: #f8fafc;
  align-items: center;
}

.item-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e0e7ff;
  color: #4338ca;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}

.verify-item-meta {
  justify-content: flex-end;
}

.meta-block {
  min-width: 110px;
}

.meta-label {
  font-size: 0.75rem;
  color: #6b7280;
}

.meta-value {
  font-weight: 600;
  color: #111827;
}

.verify-checklist-card {
  padding: 1rem;
  border-radius: 1rem;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  margin: 0;
}

.verify-checklist-title {
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.75rem;
}

.verify-checklist-grid {
  display: grid;
  gap: 0.5rem;
}

.verify-checklist-row {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 1rem;
  align-items: center;
  padding: 0.75rem;
  border-radius: 0.75rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
}

.verify-checklist-index {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.85rem;
}

.verify-checklist-label {
  color: #334155;
  font-size: 0.95rem;
}

.verify-checklist-value {
  text-align: right;


  font-size: 12px;
}
.attachment-tile {
  transition: transform 0.2s, box-shadow 0.2s;
}
.attachment-tile:hover {
  transform: translateY(-4px);
}
.attachment-tile:hover .attachment-preview {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
.attachment-preview {
  transition: box-shadow 0.2s;
}
.invoice-details {
  border-color: #dee2e6;
}
</style>
